import React from "react";
import { Entry, EntryCollection } from "contentful";
import { NextPage, NextPageContext } from "next";
import { slugify } from "helpers/slugify";
import { getUrlMask } from "components/blog/pagination";
import BlogLayout from "layouts/BlogLayout";
import SearchResults from "components/blog/searchresults";
import { Crumb } from "components/Breadcrumb";
import { getContentfulEntries } from "services/contentful/client/contentfulClient";
import {
    IContentBlockFaqListFields,
    IContentBlockLinkListFields,
    IPostFields
} from "type-definitions/generated/contentful";
import { BlogPostType, EnhancesDyanmicContentListProps } from "components/blog/BlogTypes";
import styles from "./index.scss";

/**
 * These exist in Blog Content -> Category on contentful.
 */
type BlogCategoryQuery =
    | "/kopen/autocoach"
    | "aankoopadvies"
    | "aanraders"
    | "advertorial"
    | "advies"
    | "auto-kiezen"
    | "autoverzekeringen"
    | "merk"
    | "nieuws"
    | "over-autotrack"
    | "rijtest"
    | "seo-text"
    | "tips"
    | "wat-is";

type PageType = "blogAanraders" | "blogAdvies" | "blogWatIs" | "undefined";

const blogPageTypesMap = (category?: string): PageType => {
    switch (category) {
        case "aanraders":
            return "blogAanraders";

        case "advies":
            return "blogAdvies";

        case "wat-is":
            return "blogWatIs";

        case "nieuws":
            return "undefined";

        default:
            return "undefined";
    }
};

const BLOG_CATEGORY_TO_CONTENTFUL_ID_MAP: Record<BlogCategoryQuery, string> = {
    "/kopen/autocoach": "2RcuGL7RfYxLaY8oe4pS3z",
    "aankoopadvies": "category_45",
    "aanraders": "66DsBJ1o1zaTSbDxS1Xx42",
    "advertorial": "tag_19",
    "advies": "category_13",
    "auto-kiezen": "6eIbivyR2CEd0EoQw6OnK0",
    "autoverzekeringen": "1HOcMnSiTBFjBrYKmIi2z2",
    "merk": "category_125",
    "nieuws": "category_4",
    "over-autotrack": "74rpJ69L6oSks8uUg0AS0G",
    "rijtest": "category_44",
    "seo-text": "1AXhiNQIkAU6wmCQOkYqsU",
    "tips": "category_6",
    "wat-is": "3Q4fLF1b9C7n14Dk7aoU3H"
};

/** maps a string type to a known type (BlogCategoryQuery) */
const getCategoryFromQuery = (queryCategory: string | undefined): BlogCategoryQuery => {
    switch (queryCategory) {
        case "advies":
            return "advies";

        case "nieuws":
            return "nieuws";

        case "aanraders":
            return "aanraders";

        case "wat-is":
            return "wat-is";

        case "tips":
            return "tips";

        case "aankoopadvies":
            return "aankoopadvies";

        case "autoverzekeringen":
            return "autoverzekeringen";

        case "rijtest":
            return "rijtest";
    }

    return "advies";
};

const getCategoryDisplayName = (category: string | undefined): string => {
    switch (category) {
        case "advies":
            return "Advies";

        case "nieuws":
            return "Nieuws";

        case "aanraders":
            return "Aanraders";

        case "wat-is":
            return "Wat is";

        case "tips":
            return "Tips";

        case "aankoopadvies":
            return "Aankoopadvies";

        case "autoverzekeringen":
            return "Autoverzekeringen";

        case "rijtest":
            return "Rijtest";
    }

    return "Advies"; // NOTE: This used to say that it defaulted to Nieuws but it actually returned Advies posts
};

const getBreadcrumbTrail = (): Crumb[] => {
    return [
        {
            title: "Home",
            mask: "/",
            url: "/",
            external: true
        },
        {
            title: "Blog",
            mask: "/blog",
            url: "/blog"
        }
    ];
};

type Props = {
    limit: number;
    skip: number;
    contentfulPosts: EntryCollection<IPostFields>;

    category?: string;
    query?: string;
};

/**
 * Transforms the contentful response to look like the previous apollo response so that I don't have
 * to change all the places this is used.
 */
export const transformContentfulPost = (
    x: Entry<
        IPostFields & {
            dynamicContentList?: (
                | (IContentBlockLinkListFields & EnhancesDyanmicContentListProps)
                | (IContentBlockFaqListFields & EnhancesDyanmicContentListProps)
            )[];
        }
    >
): BlogPostType => {
    return {
        id: x.sys.id,
        title: x.fields.title || "",
        content: x.fields.content || "",
        ...(x.fields.dynamicContentList && { dynamicContentList: x.fields.dynamicContentList }),
        slug: x.fields.slug,
        categories: (x.fields.categories || []).map((y) => ({
            name: y?.fields?.name,
            slug: y?.fields?.slug,
            __typename: "Category"
        })),
        imageSrc: x.fields.attachment?.fields.file.url,
        author: {
            display_name: x.fields.author?.fields.display_name,
            ...(x.fields.author?.fields.slug && { slug: x.fields.author?.fields.slug }),
            __typename: "Author"
        },
        publishDate: x.fields.created_at,
        metaDescription: x.fields.metaDescription,
        __typename: "Post"
    };
};

// @ts-ignore
const Search: NextPage<Props> = (props) => {
    const { query, skip, limit, category, contentfulPosts } = props;

    const breadcrumbTrail = getBreadcrumbTrail();
    const pageType = blogPageTypesMap(category);

    let title = "";
    let content = null;
    let categoryName = category;

    if (contentfulPosts) {
        // Display name now matches the category all the time. Defaulting to Nieuws was wrong because it defaults to getting Advies posts
        categoryName = getCategoryDisplayName(category);

        title = query ? `Zoekresultaten voor ${query}` : `${categoryName} overzicht`;
        breadcrumbTrail.push({
            title,
            active: true
        });

        const posts = contentfulPosts.items.map(transformContentfulPost);

        content = (
            <SearchResults
                {...props}
                category={slugify(categoryName ?? "")}
                total={contentfulPosts.total}
                // @ts-ignore this component is not yet typed
                posts={posts}
            />
        );
    }

    return (
        <BlogLayout
            title={title}
            mainClass="results"
            category={categoryName}
            searchCategory={categoryName}
            breadcrumbTrail={breadcrumbTrail}
            pageName="blog-zoeken"
            pageType={pageType}
            canonicalUrl={getUrlMask(slugify(categoryName ?? ""), query, skip, limit)}
        >
            <style jsx>{styles}</style>
            {content}
        </BlogLayout>
    );
};

// @ts-ignore
Search.getInitialProps = async ({ query }: NextPageContext) => {
    const limit = query.limit ? Number(query.limit) : 15;
    const skip = query.skip ? Number(query.skip) : query.page ? Number(query.page) * limit : 0;

    // The existing behavior is to only get the first category
    const category = Array.isArray(query.category)
        ? query.category[0]
        : (query.category || "").split(",").shift();

    const blogPostsCategory = getCategoryFromQuery(category);

    const contenfulCategoryId = BLOG_CATEGORY_TO_CONTENTFUL_ID_MAP[blogPostsCategory];

    const params = {
        "contentType": "post",
        "include": 3,
        "select": "sys.id,fields",
        "fields.categories.sys.id[in]": contenfulCategoryId, // get all the posts with this category
        limit,
        skip,
        "query": Array.isArray(query.query) ? query.query[0] : query.query,
        "order": "-fields.created_at" // sort by this field
    };

    const contentfulPosts = await getContentfulEntries(params);

    return {
        category,
        query: Array.isArray(query.query) ? query.query[0] : query.query,
        limit,
        skip,
        contentfulPosts
    };
};

Search.displayName = "Search";

export default Search;
